import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import {
  BookOpen, ClipboardPlus, Search, ShieldAlert, HelpCircle,
  MessageSquare, ListChecks, Sparkles, AlertTriangle, Route, PlayCircle,
} from "lucide-react";

/* ------------------------------------------------------------------
 * ConfirmPH Field Promoter: Sales Guide (mobile-first)
 * Single source of truth for pricing: /api/promoter/plans -> billing/constants
 * ------------------------------------------------------------------ */

function DemoLink({ id, label = "Show demo", testId }) {
  return (
    <Link to={`/promoter/demos/${id}`}
          data-testid={testId || `guide-demo-link-${id}`}
          className="inline-flex items-center gap-1 rounded-full bg-emerald-700 text-white text-[11px] px-2 py-0.5 hover:bg-emerald-800">
      <PlayCircle className="w-3 h-3" /> {label}
    </Link>
  );
}

const SECTIONS = [
  { id: "quick",       label: "Quick Reference",        icon: Sparkles },
  { id: "before",      label: "Before You Enter",       icon: ListChecks },
  { id: "opening",     label: "Opening the Conversation", icon: MessageSquare },
  { id: "discovery",   label: "Questions to Ask",       icon: HelpCircle },
  { id: "explain",     label: "Always Explain Clearly", icon: BookOpen },
  { id: "demo",        label: "What To Demonstrate",    icon: Route },
  { id: "activation",  label: "Main Activation Ask",    icon: Sparkles },
  { id: "outcomes",    label: "Visit Outcomes",         icon: ListChecks },
  { id: "faq",         label: "FAQs & Approved Answers", icon: HelpCircle },
  { id: "clarify",     label: "Important Clarifications", icon: ShieldAlert },
  { id: "never",       label: "NEVER Say This",         icon: AlertTriangle },
  { id: "dont-guess",  label: "If You Don't Know",      icon: HelpCircle },
  { id: "objections",  label: "Objection Handling",     icon: MessageSquare },
  { id: "plans",       label: "Current Plans (Live)",   icon: BookOpen },
  { id: "checklist",   label: "Field Visit Checklist",  icon: ListChecks },
];

export default function PromoterSalesGuide() {
  const [query, setQuery] = useState("");
  const [plans, setPlans] = useState(null);
  const [plansErr, setPlansErr] = useState("");

  useEffect(() => {
    api.get("/promoter/plans")
      .then(({ data }) => setPlans(data.items || []))
      .catch(e => setPlansErr(e?.response?.data?.detail || "Unable to load plans right now."));
  }, []);

  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () => (q ? SECTIONS.filter(s => s.label.toLowerCase().includes(q)) : SECTIONS),
    [q]
  );
  const openAll = visible.map(s => s.id);

  return (
    <div data-testid="promoter-sales-guide-page" className="pb-8">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">
            Sales Guide
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Field reference for ConfirmPH promoters. Open the section you need before or during a visit.
          </p>
        </div>
        <Link to="/promoter/new-log"
              data-testid="guide-shortcut-log-visit"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-white text-xs px-3 py-2 font-medium">
          <ClipboardPlus className="w-4 h-4" /> Log This Visit
        </Link>
      </div>

      {/* Search */}
      <div className="mt-5 relative max-w-md" data-testid="guide-search-wrap">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query} onChange={e => setQuery(e.target.value)}
          data-testid="guide-search"
          placeholder="Search sections (e.g. objection, price, FAQ)…"
          className="w-full rounded-full border border-slate-300 pl-9 pr-3 py-2 text-sm bg-white" />
      </div>

      {/* Accordion: sections auto-expand while a search is active */}
      <Accordion type="multiple"
                  defaultValue={["quick", "demo"]}
                  value={q ? openAll : undefined}
                  className="mt-5 space-y-2"
                  data-testid="guide-accordion">
        {visible.map(s => (
          <AccordionItem key={s.id} value={s.id}
                          data-testid={`guide-section-${s.id}`}
                          className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <AccordionTrigger className="px-4 py-3 hover:no-underline text-left"
                                data-testid={`guide-trigger-${s.id}`}>
              <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <s.icon className="w-4 h-4 text-[#1B4332]" /> {s.label}
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 pt-1 text-sm text-slate-700 leading-relaxed">
              {renderSection(s.id, { plans, plansErr })}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function renderSection(id, { plans, plansErr }) {
  switch (id) {
    case "quick":      return <QuickReference />;
    case "before":     return <BeforeYouEnter />;
    case "opening":    return <Opening />;
    case "discovery":  return <Discovery />;
    case "explain":    return <Explain />;
    case "demo":       return <Demonstrate />;
    case "activation": return <Activation />;
    case "outcomes":   return <Outcomes />;
    case "faq":        return <FAQ />;
    case "clarify":    return <Clarify />;
    case "never":      return <NeverSay />;
    case "dont-guess": return <DontGuess />;
    case "objections": return <Objections />;
    case "plans":      return <PlansSection plans={plans} plansErr={plansErr} />;
    case "checklist":  return <Checklist />;
    default:           return null;
  }
}

function Bullets({ items, testId }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5" data-testid={testId}>
      {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
  );
}

/* -------- Sections -------- */

function QuickReference() {
  return (
    <div className="space-y-4" data-testid="quick-reference">
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
        <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">Main message</div>
        <p className="mt-1 text-slate-800">
          ConfirmPH helps rental businesses properly record, confirm, track, and follow up on
          bookings, without forcing them to stop using Messenger, Facebook, WhatsApp, Viber,
          phone, or walk-ins.
        </p>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Three things to remember</div>
        <ol className="list-decimal pl-5 mt-1 space-y-1">
          <li>Ask how they currently manage bookings before pitching.</li>
          <li>Show the feature connected to their actual problem.</li>
          <li>Never promise something ConfirmPH does not guarantee.</li>
        </ol>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Main goal</div>
        <p className="mt-1">
          Success isn&apos;t just an account. Aim for: <em>Account → Setup → First vehicle → First
          real booking → Customer confirmation → Continued use.</em>
        </p>
      </div>
    </div>
  );
}

function BeforeYouEnter() {
  return (
    <Bullets testId="before-list" items={[
      "Know the business name if possible.",
      "Check that you are at the correct business.",
      "Ask for the owner or manager.",
      "Be friendly and professional.",
      "Do not immediately give a long sales pitch: ask questions first, then listen.",
      "Keep the conversation relevant.",
      "Have the ConfirmPH demo ready.",
      "Have signup QR/link ready.",
      "Ensure phone battery/data is sufficient.",
      "Know the current plans and prices (see Current Plans below).",
      "Never invent an answer.",
    ]} />
  );
}

function Opening() {
  return (
    <div className="space-y-3" data-testid="opening">
      <p className="italic text-slate-700 bg-slate-50 border border-slate-200 rounded p-3">
        “Hi, I&apos;m with ConfirmPH. We help rental businesses keep their bookings properly recorded
        and confirmed with customers. Can I ask how you currently keep track of your bookings?”
      </p>
      <p className="text-slate-600 text-xs">
        You don&apos;t have to say it word-for-word. Keep it natural. Let them talk first.
      </p>
    </div>
  );
}

function Discovery() {
  return (
    <Bullets testId="discovery-list" items={[
      "How do customers normally book with you?",
      "How do you currently keep track of bookings?",
      "Do you mainly use Messenger or Facebook?",
      "Do multiple staff members handle customer messages?",
      "Have bookings ever been forgotten?",
      "Have two customers ever been promised the same vehicle?",
      "How do customers know their booking is definitely recorded?",
      "What happens when a vehicle returns late?",
      "What happens when a booked vehicle suddenly needs maintenance?",
      "How do staff know who needs follow-up?",
      "Where do you record deposits and remaining balances?",
      "How do you keep proof of what was agreed with the customer?",
    ]} />
  );
}

function Explain() {
  return (
    <div className="space-y-3" data-testid="explain">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-slate-800">
        ConfirmPH does <b>not</b> replace Messenger, Facebook, WhatsApp, Viber, phone, or
        walk-in bookings. Businesses can keep receiving customers exactly as they do today.
        ConfirmPH adds a <b>structured booking layer</b> on top.
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">ConfirmPH helps with</div>
        <Bullets items={[
          "Proper booking records",
          "Customer confirmation links",
          "Booking status",
          "Double-booking detection",
          "Tight-buffer warnings",
          "Pickup / return reminders",
          "Staff follow-up",
          "Vehicle records & status",
          "Incidents & affected bookings",
          "Deposits / balance records",
          "Activity history",
          "Booking paper trail",
          "Public profile / Discover Rentals (where available)",
          "Booking-linked reviews (where available)",
        ]} />
      </div>
    </div>
  );
}

function Demonstrate() {
  const rows = [
    ["Messenger confusion", "Booking Intake → Booking → Customer Confirmation", "messenger-inquiry"],
    ["Worried about double bookings", "Conflict / overlap warnings", "double-booking"],
    ["Employees forget things", "Follow-ups / reminders", "follow-up-reminder"],
    ["Vehicles sometimes unavailable", "Vehicle status → incident → affected bookings", "vehicle-breakdown"],
    ["Wants organization", "Bookings + Vehicles + Activity", "multi-staff-activity"],
    ["Wants customers to find them", "Public Profile / Discover Rentals (only if their plan supports it)", null],
    ["Wants reviews", "Booking-linked reviews (explain accurately)", "verified-review"],
  ];
  return (
    <div className="space-y-2" data-testid="demo-map">
      <p className="text-slate-600 text-xs">Show what solves <em>their</em> pain, not every screen.</p>
      <div className="divide-y divide-slate-200 rounded border border-slate-200 bg-white">
        {rows.map(([problem, feature, demoId]) => (
          <div key={problem} className="p-2.5 grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-3">
            <div className="text-slate-500 text-xs">{problem}</div>
            <div className="text-slate-900 font-medium flex items-center gap-2 flex-wrap">
              <span>{feature}</span>
              {demoId && <DemoLink id={demoId} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Activation() {
  return (
    <div className="space-y-3" data-testid="activation">
      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
        <p className="text-slate-800">
          Preferred early close: <b>“You can start on the Free plan. Let&apos;s set up your business
          and try it with your next real booking.”</b>
        </p>
      </div>
      <Bullets items={[
        "Create/register account",
        "Add business details",
        "Add first vehicle",
        "Create first real booking where appropriate",
        "Send confirmation",
        "Show them where to find it again",
      ]} />
    </div>
  );
}

function Outcomes() {
  return (
    <div data-testid="outcomes">
      <p className="text-slate-600 text-xs mb-2">
        Record the honest result in your Visit Log, never fake a signup to improve metrics.
      </p>
      <Bullets items={[
        "Owner unavailable",
        "Manager unavailable",
        "Conversation completed",
        "Interested",
        "Not interested",
        "Follow-up needed",
        "Demo completed",
        "Signup completed",
        "Onboarding started",
        "First vehicle added",
        "First booking created",
        "First confirmation sent",
        "Active business",
        "Upgrade discussion",
        "Paying business",
      ]} />
    </div>
  );
}

function FAQ() {
  const items = [
    ["We already use Messenger.",
      "That's fine: you can keep using Messenger. ConfirmPH isn't replacing how customers contact you. It gives you one structured place to properly record what was agreed and send the customer a confirmation.",
      "messenger-inquiry"],
    ["Messenger is free. Why should I pay?",
      "Messenger is designed for communication. It isn't designed to manage vehicle availability, booking conflicts, customer confirmations, incidents, reminders, staff follow-up, and booking history. ConfirmPH adds that management layer, and the Free plan lets them try it first.",
      null],
    ["Do I have to stop using Facebook?",
      "No. You can continue receiving bookings through Facebook and Messenger exactly as you do now.",
      null],
    ["Does ConfirmPH bring customers?",
      "ConfirmPH may provide public visibility through features such as Discover Rentals and public profiles where available, but ConfirmPH does not guarantee customers, leads, bookings, or revenue.",
      null],
    ["Can ConfirmPH prevent double bookings?",
      "ConfirmPH checks bookings recorded in the system for overlaps and scheduling conflicts and can warn you before a conflict becomes a customer problem. Important: ConfirmPH cannot detect a booking that was never entered into ConfirmPH.",
      "double-booking"],
    ["What if I forget to enter a Messenger booking?",
      "ConfirmPH cannot know about information that was never entered or imported. That's why getting bookings into the intake system is important.",
      "forgotten-messenger-booking"],
    ["Does the customer need an account?",
      "Use the current live implementation. Where applicable, secure customer booking/confirmation flows can work without requiring a full business account.",
      null],
    ["Can my employees use it?",
      "Show current staff-account limits for their plan (see Current Plans). Never promise more staff accounts than their plan allows.",
      "multi-staff-activity"],
    ["Are we officially verified by ConfirmPH?",
      "No. Using ConfirmPH does not mean ConfirmPH guarantees or officially certifies your business. Trust signals provide information about how a business uses ConfirmPH; they are not guarantees.",
      null],
    ["Does ConfirmPH guarantee my vehicles are safe?",
      "No. Vehicle condition, maintenance, documents, insurance, roadworthiness, safety, and compliance remain the rental business's responsibility.",
      null],
    ["Can you guarantee no double booking?",
      "No. ConfirmPH reduces the risk by detecting conflicts in bookings recorded in the system, but it cannot guarantee that a business will never experience a booking problem.",
      "double-booking"],
    ["Does ConfirmPH handle renter payments?",
      "Use the current live feature status. Never claim ConfirmPH holds or processes renter rental payments unless the feature is actually enabled.",
      "deposit-balance"],
    ["Can I cancel?",
      "Point them to the current Business Terms, Billing Policy, Refund Policy, and their plan settings.",
      null],
    ["How much does ConfirmPH cost?",
      "Open Current Plans: pricing is loaded live from ConfirmPH's central config so you always show accurate figures.",
      null],
  ];
  return (
    <div className="space-y-2" data-testid="faq-list">
      {items.map(([q, a, demoId], i) => (
        <details key={i} className="rounded border border-slate-200 bg-white p-3">
          <summary className="cursor-pointer text-sm font-medium text-slate-900">{q}</summary>
          <p className="mt-2 text-slate-700 text-sm leading-relaxed">{a}</p>
          {demoId && <div className="mt-2"><DemoLink id={demoId} /></div>}
        </details>
      ))}
    </div>
  );
}

function Clarify() {
  return (
    <div data-testid="clarify" className="space-y-3">
      <p className="text-slate-700">ConfirmPH does <b>not</b> guarantee:</p>
      <Bullets items={[
        "Business legitimacy",
        "More customers",
        "More revenue",
        "Vehicle availability",
        "Vehicle condition",
        "Insurance",
        "Document validity",
        "Customer payment",
        "Deposit return",
        "Refunds",
        "Perfect booking accuracy",
        "That every staff member enters every booking",
        "Dispute outcomes",
        "Legal compliance",
      ]} />
      <p className="text-slate-600 text-xs italic">
        ConfirmPH is a booking confirmation and reliability platform, not a government
        authority, insurer, rental operator, lawyer, or regulator.
      </p>
    </div>
  );
}

function NeverSay() {
  const items = [
    "“ConfirmPH guarantees your business.”",
    "“You're officially verified.”",
    "“Your vehicles are certified by ConfirmPH.”",
    "“You'll definitely get more customers.”",
    "“You can't get double-booked anymore.”",
    "“All your customers will pay.”",
    "“ConfirmPH insures your rentals.”",
    "“ConfirmPH guarantees refunds.”",
    "“We guarantee your business is safe.”",
    "“ConfirmPH handles all disputes.”",
  ];
  return (
    <div data-testid="never-say" className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3">
      <div className="flex items-center gap-2 text-rose-900 font-semibold">
        <AlertTriangle className="w-4 h-4" /> Never tell a business:
      </div>
      <ul className="mt-2 list-disc pl-5 space-y-1 text-rose-900">
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
      <p className="mt-3 text-xs text-rose-800">Sell the real product. Do not manufacture benefits that do not exist.</p>
    </div>
  );
}

function DontGuess() {
  return (
    <div data-testid="dont-guess" className="space-y-2">
      <p>If a business asks something you don&apos;t know:</p>
      <Bullets items={[
        "Don't guess. Don't invent an answer.",
        "Say: “I don't want to give you incorrect information. Let me confirm that for you.”",
        "Record the question.",
        "Contact the appropriate ConfirmPH manager/support channel.",
        "Follow up with the correct answer.",
      ]} />
    </div>
  );
}

function Objections() {
  const items = [
    ["We're too small.",
      "That's okay. ConfirmPH has a Free plan specifically so small businesses can start with the core booking workflow before deciding whether they need more.",
      "messenger-inquiry"],
    ["This looks complicated.",
      "You don't need to use everything at once. Start with one vehicle and your next booking. The basic flow is simply: record booking → send confirmation → customer confirms.",
      "messenger-inquiry"],
    ["My staff won't use it.",
      "You can start with one person managing ConfirmPH. If it becomes useful, you can introduce it to additional staff gradually.",
      "multi-staff-activity"],
    ["We've never forgotten a booking.",
      "That's good. ConfirmPH is also useful for written confirmations, booking history, reminders, vehicle status, customer follow-up, and maintaining a clearer paper trail.",
      "forgotten-messenger-booking"],
    ["I need to think about it.",
      "Of course. Would it be okay if I follow up with you on [day/date]? Record the agreed follow-up.",
      null],
    ["Not interested.",
      "Don't argue. Respond professionally. Thank them. Record the actual result. Move on.",
      null],
  ];
  return (
    <div className="space-y-2" data-testid="objections">
      {items.map(([o, a, demoId], i) => (
        <details key={i} className="rounded border border-slate-200 bg-white p-3">
          <summary className="cursor-pointer text-sm font-medium text-slate-900">{o}</summary>
          <p className="mt-2 text-slate-700 text-sm leading-relaxed">{a}</p>
          {demoId && <div className="mt-2"><DemoLink id={demoId} /></div>}
        </details>
      ))}
    </div>
  );
}

function PlansSection({ plans, plansErr }) {
  if (plansErr) return <div className="text-rose-700 text-sm" data-testid="plans-error">{plansErr}</div>;
  if (!plans)   return <div className="text-slate-500 text-sm" data-testid="plans-loading">Loading current plans…</div>;

  const fmt = (n) => (n === 0 ? "Free" : `₱${Number(n).toLocaleString("en-PH")}/mo`);
  const cap = (v) => (v === null || v === undefined ? "Unlimited" : v);

  return (
    <div className="space-y-3" data-testid="plans-list">
      <p className="text-xs text-slate-500">Live from ConfirmPH&apos;s central pricing config.</p>
      {plans.map(p => (
        <div key={p.key} data-testid={`plan-${p.key}`}
              className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-semibold text-slate-900">{p.name}</div>
            <div className="text-sm text-emerald-800 font-medium">
              {p.price_label || fmt(p.price_php_monthly)}
            </div>
          </div>
          {p.summary && <div className="text-xs text-slate-600 mt-1">{p.summary}</div>}
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded bg-slate-50 border border-slate-200 p-2">
              <div className="uppercase tracking-wider text-slate-500">Fleet points</div>
              <div className="mt-0.5 text-slate-900 font-medium">{cap(p.fleet_points)}</div>
            </div>
            <div className="rounded bg-slate-50 border border-slate-200 p-2">
              <div className="uppercase tracking-wider text-slate-500">Staff accounts</div>
              <div className="mt-0.5 text-slate-900 font-medium">{cap(p.staff_limit)}</div>
            </div>
          </div>
          {p.features?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-slate-600">See features</summary>
              <ul className="mt-1 list-disc pl-5 text-xs text-slate-700 space-y-0.5">
                {p.features.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </details>
          )}
        </div>
      ))}
      <p className="text-[11px] text-slate-500 italic">
        Do not quote figures from memory: always open this section for accurate prices.
      </p>
    </div>
  );
}

function ChecklistSection({ title, items, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={testId}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
      <ul className="mt-2 space-y-1.5 text-slate-800 text-sm">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-1 w-3.5 h-3.5 rounded border border-slate-400 flex-shrink-0" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Checklist() {
  return (
    <div className="space-y-3" data-testid="checklist">
      <ChecklistSection testId="checklist-before" title="Before" items={[
        "Correct business",
        "Owner/manager target",
        "Demo ready",
        "Signup QR/link ready",
        "Pricing loaded",
        "Battery/data",
        "Professional presentation",
      ]} />
      <ChecklistSection testId="checklist-during" title="During" items={[
        "Ask about current booking process",
        "Listen",
        "Identify the real problem",
        "Demonstrate relevant solution",
        "Explain Free option",
        "Explain important limitations",
        "Ask for next action",
      ]} />
      <ChecklistSection testId="checklist-after" title="After" items={[
        "Submit Visit Log",
        "Record correct outcome",
        "Record decision-maker",
        "Record contact",
        "Record objection",
        "Add follow-up date if agreed",
        "Record signup",
        "Record activation progress",
      ]} />

      <Link to="/promoter/new-log"
            data-testid="checklist-log-visit"
            className="inline-flex items-center gap-2 rounded-full bg-[#1B4332] text-white text-sm px-4 py-2 font-medium">
        <ClipboardPlus className="w-4 h-4" /> Log This Visit
      </Link>
    </div>
  );
}
